import React, { useEffect, useCallback, useState, useMemo } from 'react'
import {
  Button,
  Col,
  Container,
  Form,
  Logo,
  Row,
  Spinner,
  useDebounce
} from 'react-angle-dashboard-components'
import { Controller, useFormContext } from 'react-hook-form'
import Select from 'react-select'
import { ErrorMessage } from '@hookform/error-message'
import { NumericFormat } from 'react-number-format'
import { IQuoteFormData } from '../../QuoteForm'
import {
  GrayText,
  Heading,
  InvalidFeedback,
  SmallGrayHeading
} from '../../style'
import GroupFormModal from '../../../../components/GroupFormModal/GroupFormModal'
import FooterNav from '../../FooterNav'
import {
  useSearchGeneralAgencies,
  useGroupChoices
} from '../../../../hooks/quote.hooks'
import { IGroupChoiceDTO } from '../../../../services/quote/dto'
import {
  getDefaultPepm,
  getEffectiveDateList,
  byobStepsCopy
} from './utils'

const dates = getEffectiveDateList()

const BasicInfoStep1 = () => {
  const { register, formState, control, watch, setValue } =
    useFormContext<IQuoteFormData>()
  const [isGroupFormModalOpen, setIsGroupFormModalOpen] = useState(false)
  const [groupIdToEdit, setGroupIdToEdit] = useState<number | undefined>(
    undefined
  )
  const [hasAgentOfRecord, setHasAgentOfRecord] = useState<boolean>(
    !!watch('basicInfo.step1.agentOfRecordName')
  )
  const [hasGeneralAgency, setHasGeneralAgency] = useState<boolean>(
    !!watch('basicInfo.step1.generalAgencyId')
  )
  const [generalAgencySearchTerm, setGeneralAgencySearchTerm] =
    useState<string>('')
  const debouncedSearchTerm = useDebounce(generalAgencySearchTerm, 300)
  const agentOfRecordName = watch('basicInfo.step1.agentOfRecordName')
  const generalAgencyId = watch('basicInfo.step1.generalAgencyId')
  const generalAgencyName = watch('basicInfo.step1.generalAgencyName')
  const [selectedGeneralAgency, setSelectedGeneralAgency] = useState<null | {
    value: string
    label: string
  }>(null)

  const generalAgencyDefaultOptions =
    generalAgencyId && generalAgencyName
      ? [{ value: String(generalAgencyId), label: String(generalAgencyName) }]
      : []

  useEffect(() => {
    if (!!agentOfRecordName) {
      setHasAgentOfRecord(true)
    }

    if (generalAgencyId && generalAgencyName) {
      setHasGeneralAgency(true)
      setSelectedGeneralAgency({
        label: String(generalAgencyName),
        value: String(generalAgencyId)
      })
    }
  }, [agentOfRecordName, generalAgencyId, generalAgencyName])

  const {
    data: generalAgenciesData,
    isLoading: isGeneralAgenciesLoading,
    refetch
  } = useSearchGeneralAgencies(debouncedSearchTerm)
  const { data: groupChoicesData, isLoading } = useGroupChoices()

  useEffect(() => {
    refetch()
  }, [refetch, debouncedSearchTerm])

  const groupChoices: IGroupChoiceDTO[] = useMemo<IGroupChoiceDTO[]>(() => {
    const groups = [...(groupChoicesData?.map((g) => g) ?? [])]
    groups.sort((a, b) => {
      if (a.groupName === b.groupName) return 0
      return a.groupName < b.groupName ? -1 : 1
    })

    return groups
  }, [groupChoicesData])

  const errors = formState.errors.basicInfo?.step1

  const onAddNewGroup = () => {
    setGroupIdToEdit(undefined)
    setIsGroupFormModalOpen(true)
  }

  const onEditGroup = () => {
    setGroupIdToEdit(watch('basicInfo.step1.groupId'))
    setIsGroupFormModalOpen(true)
  }

  const onGeneralAgencySearch = useCallback((searchTerm: string) => {
    setGeneralAgencySearchTerm(searchTerm)
  }, [])

  const groupId = watch('basicInfo.step1.groupId')

  const selectedGroup = useMemo(() => groupChoices?.find(
    (gc) => gc.entryId === groupId
  ), [groupChoices, groupId])

  useEffect(() => {
    const defaultPepmValue = getDefaultPepm(selectedGroup)
    setValue('basicInfo.step1.pepmPayable', defaultPepmValue)
  }, [selectedGroup, setValue])

  const disabled = !!watch('entryId')

  return (
    <Container className="p-4">
      <GroupFormModal
        key={JSON.stringify(isGroupFormModalOpen)}
        open={isGroupFormModalOpen}
        onClose={() => setIsGroupFormModalOpen(false)}
        groupIdToEdit={groupIdToEdit}
      />
      {isLoading ? (
        <Spinner animation="border" />
      ) : (
        <Row style={{ paddingTop: '1rem', paddingBottom: '8rem' }}>
          <Col sm={4}>
            <SmallGrayHeading>Basic Quote Info (1/3)</SmallGrayHeading>
            <Heading className="pt-1">Select Your Group</Heading>
            {disabled && (
              <InvalidFeedback>
                These fields cannot be changed after continuing to the next
                step. If you need to modify these entries, you will need to
                create a new quote.
              </InvalidFeedback>
            )}
            <Form.Group className="pt-5">
              <div className="d-flex justify-content-between">
                <Form.Label className="d-flex align-items-end">
                  Group Name
                </Form.Label>
                {!disabled && (
                  <Button
                    variant="link d-flex align-items-center font-weight-normal pb-0 pr-0"
                    onClick={onAddNewGroup}
                  >
                    <i
                      className="icon icon-plus fs-12 mr-2"
                      style={{ height: '100%', width: '0.8rem' }}
                    />
                    <span>Add a group</span>
                  </Button>
                )}
              </div>
              <fieldset disabled={disabled}>
                <Form.Control
                  as="select"
                  isInvalid={!!errors?.groupId}
                  {...register('basicInfo.step1.groupId', {
                    valueAsNumber: true
                  })}
                >
                  <option value=""></option>
                  {groupChoices?.map((g) => (
                    <option key={g.entryId} value={g.entryId}>
                      {g.groupName}
                    </option>
                  ))}
                </Form.Control>
              </fieldset>
              <Form.Control.Feedback type="invalid">
                {errors?.groupId?.message}
              </Form.Control.Feedback>
              {!!watch('basicInfo.step1.groupId') && !disabled && (
                <Button
                  variant="link d-flex align-items-center font-weight-normal pb-0 px-0"
                  onClick={onEditGroup}
                >
                  Edit the selected group
                </Button>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Effective Date</Form.Label>
              <fieldset disabled={disabled}>
                <Form.Control
                  as="select"
                  isInvalid={!!errors?.effectiveDate}
                  {...register('basicInfo.step1.effectiveDate')}
                >
                  <option value=""></option>
                  {dates?.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Form.Control>
              </fieldset>
              <Form.Control.Feedback type="invalid">
                {errors?.effectiveDate?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label>Broker PEPM ($)</Form.Label>
              <fieldset disabled={disabled}>
                <Controller
                  name="basicInfo.step1.pepmPayable"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <NumericFormat
                      style={
                        errors?.pepmPayable ? { borderColor: '#dc3545' } : {}
                      }
                      customInput={Form.Control}
                      thousandSeparator=","
                      decimalSeparator="."
                      prefix="$"
                      decimalScale={2}
                      getInputRef={ref}
                      {...rest}
                    />
                  )}
                />
              </fieldset>
              <ErrorMessage
                errors={errors}
                name="pepmPayable"
                render={({ message }) => (
                  <p className="text-danger">{message}</p>
                )}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                Are you creating this quote on behalf of another producer?
              </Form.Label>
              <Form.Check
                type="radio"
                name="hasAgentOfRecord"
                value="yes"
                label="Yes"
                defaultChecked={hasAgentOfRecord}
                onChange={() => setHasAgentOfRecord(true)}
              />
              <Form.Check
                type="radio"
                name="hasAgentOfRecord"
                value="no"
                label="No"
                defaultChecked={!hasAgentOfRecord}
                onChange={() => {
                  setHasAgentOfRecord(false)
                  setValue('basicInfo.step1.agentOfRecordName', '')
                  setValue('basicInfo.step1.agentOfRecordEmailAddress', '')
                }}
              />
            </Form.Group>
            {hasAgentOfRecord && (
              <>
                <Form.Group>
                  <Form.Label>Agent of Record Full Name</Form.Label>
                  <Form.Control
                    {...register('basicInfo.step1.agentOfRecordName')}
                    isInvalid={!!errors?.agentOfRecordName}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Agent of Record Email Address</Form.Label>
                  <Form.Control
                    {...register('basicInfo.step1.agentOfRecordEmailAddress')}
                    isInvalid={!!errors?.agentOfRecordEmailAddress}
                  />
                </Form.Group>
              </>
            )}
            <Form.Group>
              <Form.Label>
                Is this group associated with a General Agency?
              </Form.Label>
              <Form.Check
                type="radio"
                name="associatedWithGeneralAgency"
                value="yes"
                label="Yes"
                defaultChecked={hasGeneralAgency}
                onChange={() => setHasGeneralAgency(true)}
              />
              <Form.Check
                name="associatedWithGeneralAgency"
                type="radio"
                value="yes"
                label="No"
                defaultChecked={!hasGeneralAgency}
                onChange={() => {
                  setHasGeneralAgency(false)
                  setValue('basicInfo.step1.generalAgencyId', undefined)
                }}
              />
            </Form.Group>
            {hasGeneralAgency && (
              <Form.Group>
                <Form.Label>General Agency Name</Form.Label>
                <Select
                  openMenuOnFocus
                  isClearable={false}
                  isLoading={isGeneralAgenciesLoading}
                  noOptionsMessage={() => 'No results available'}
                  placeholder="Search for General Agencies"
                  inputValue={generalAgencySearchTerm}
                  onInputChange={onGeneralAgencySearch}
                  value={selectedGeneralAgency}
                  onChange={(selected: any) => {
                    setSelectedGeneralAgency(selected)
                    setValue(
                      'basicInfo.step1.generalAgencyId',
                      Number(selected.value)
                    )
                  }}
                  components={{
                    IndicatorSeparator: () => null
                  }}
                  options={[
                    ...generalAgencyDefaultOptions,
                    ...(generalAgenciesData || [])?.map((generalAgency) => ({
                      value: String(generalAgency.entryId),
                      label: String(generalAgency.name)
                    }))
                  ]}
                  filterOption={() => true}
                />
              </Form.Group>
            )}
          </Col>
          <Col>
            <div
              className="ml-10 p-4"
              style={{
                maxWidth: 580,
                backgroundColor: '#F6F2EC'
              }}
            >
              <Col>
                <Row>
                  <Logo variant="simple" color="primary" />
                </Row>
                <Row>
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: 20
                    }}
                  >
                    Build your own benefits - Tips
                  </span>
                </Row>
                <Row>
                  <Row>
                    <Col>
                      <GrayText>
                        Our quoting tool is designed to assist you in quickly
                        and easily generating a quote tailored to your group's
                        specific needs. The quoting process is divided into five
                        sections to streamline your experience:
                      </GrayText>
                    </Col>
                  </Row>
                  <br />
                  <Row>
                    <Col>
                      {byobStepsCopy.map(({ step, title, desc }) => (
                        <Row key={step} className="my-3">
                          <Col
                            className="col-auto"
                            style={{
                              marginTop: 5
                            }}
                          >
                            <div
                              className="rounded-circle bg-primary d-flex justify-content-center align-items-center text-white"
                              style={{
                                width: 20,
                                height: 20
                              }}
                            >
                              {step}
                            </div>
                          </Col>
                          <Col className="pl-0">
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 600
                              }}
                            >
                              {title}
                            </div>
                            <GrayText
                              style={{
                                fontSize: 16
                              }}
                            >
                              {desc}
                            </GrayText>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <GrayText>
                        Once you have completed all the sections and received a
                        positive response, you can proceed to finalize the
                        process, indicating your group's intent to move forward
                        with Angle Health.
                      </GrayText>
                    </Col>
                  </Row>
                </Row>
              </Col>
            </div>
          </Col>
        </Row>
      )}
      <FooterNav triggerPath="basicInfo.step1" />
    </Container>
  )
}

export default BasicInfoStep1
